<script lang="ts" setup>
import { computed, onMounted, onErrorCaptured } from "vue"
import { useRoute } from "vue-router"

import HoldingPage from "./views/HoldingPage.vue"

import { getCurrentInstance } from "vue"
import { useProjects } from "./store/projects"
import ModalMenu from "./components/ModalMenu.vue"
import Menu from "./components/Menu/Menu.vue"
import { usePageReady } from "./composables/use-page-ready"

const route = useRoute()
const isHoldingPage = computed(
  () => import.meta.env.VITE_HOLDING_PAGE === "true" && route.path === "/",
)

const { pageReady, setPending, setReady } = usePageReady()

// Safety net: if a view's top-level await throws, Suspense never emits @resolve,
// which would strand the overlay. Clear it on a captured error so the app stays usable.
onErrorCaptured(() => {
  setReady()
  return false
})

const { appContext } = getCurrentInstance()!
const routes: string[] =
  appContext.config.globalProperties.$site.programmeRoutes.map(
    ({ uri }: any) => uri,
  )

const isProgrammePage = computed(() => {
  const path = route.path.slice(1) // Remove leading slash
  if (path === "programmes") return true
  return routes.includes(path)
})

onMounted(async () => {
  const { loadProjects } = await useProjects()
  try {
    console.time("Loading projects")
    await loadProjects()
    console.timeEnd("Loading projects")
  } catch (error) {
    console.error("Error loading projects:", error)
  }
})
</script>

<template>
  <HoldingPage v-if="isHoldingPage" />

  <div v-else class="flex flex-col min-h-dvh font-serif text-black bg-white">
    <Transition name="pageload">
      <div
        v-if="!pageReady"
        class="fixed inset-0 z-50 bg-white/50 pointer-events-none"
      />
    </Transition>

    <Menu />

    <div class="relative flex-1">
      <router-view v-slot="{ Component }">
        <!-- No #fallback / no timeout: Suspense keeps the previous view mounted while
             the next view's top-level awaits run, with the overlay layered on top.
             Contract: page data must be awaited in setup so @resolve = real readiness.
             v-if="Component" avoids a comment-only Suspense slot before the router
             resolves its first route (otherwise Vue warns about a single root node). -->
        <template v-if="Component">
          <Suspense @pending="setPending" @resolve="setReady">
            <component :is="Component" :key="route.path" />
          </Suspense>
        </template>
      </router-view>
    </div>
  </div>
</template>

<style>
.pageload-enter-active {
  transition: opacity 120ms ease;
}
.pageload-leave-active {
  transition: opacity 250ms ease;
}
.pageload-enter-from,
.pageload-leave-to {
  opacity: 0;
}

.quickfade-enter-active,
.quickfade-leave-active {
  transition: opacity 50ms ease;
}

.quickfade-enter-from,
.quickfade-leave-to {
  transition: opacity 350ms ease;
  opacity: 0;
}
</style>
